import { useEffect, useRef } from "react";

/**
 * Generative dithered avatars — a mirrored 8×8 pixel glyph derived from a name.
 *
 * The generator is dither-kit's `avatar` item (tripwire.sh/dither-kit, by
 * ripgrim, `npx @dither-kit/cli add avatar`). The kit ships it as a shadcn
 * registry component that wants Tailwind, `clsx` and `tailwind-merge`; this app
 * has none of those and is not growing three dependencies for a logo, so the
 * model and the paint loop are ported and the composition around them is
 * written in this codebase's own idiom. The numbers are the kit's — same name
 * gives the same avatar here as it does there.
 *
 * Two renderers over one model:
 *
 *   - {@link DitherAvatar} paints to a canvas, the way the kit does, with the
 *     Bayer-ordered materialize entrance. This is the one to use for a
 *     name-seeded avatar at a real size.
 *   - {@link DitherMark} emits the same glyph as SVG rects, one per cell. The
 *     brand mark is drawn at 17–30 px in the nav, the footer and the masthead,
 *     and a 32 px backing canvas scaled to 20 CSS px is neither crisp nor
 *     pixel-aligned — vector is. At that size the sub-cell dither is below a
 *     pixel anyway, so the cell carries the density it would have averaged to.
 *
 * Bloom is not ported. The kit composites the glow with `plus-lighter`, which
 * only reads as a glow against a dark ground, and saucer's palette inverts
 * between themes.
 */

/**
 * 8×8 cells, mirrored across one axis → 32 free pattern bits. With the mirror
 * axis bit and 180 hues that is 2^33 × 180 ≈ 1.5 trillion distinct avatars.
 */
const GRID = 8;
/** Backing px per cell → a 32×32 canvas, scaled up `pixelated`. */
const CELL_PX = 4;

/** 4×4 ordered (Bayer) matrix, normalised to 0–1 thresholds. */
const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((row) => row.map((v) => (v + 0.5) / 16));

type Rgb = [number, number, number];

const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

const rgba = ([r, g, b]: Rgb, a: number) => `rgba(${r},${g},${b},${a})`;

const reduceMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;

/** 32-bit FNV-1a — turns any string seed into a stable uint32. */
function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Tiny deterministic PRNG (xorshift32) — floats in [0, 1). */
function xorshift32(seed: number): () => number {
  let s = seed || 0x9e3779b9;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 0x100000000;
  };
}

/** Hue (0–360) → the rgb the dither is drawn in, at a fixed 85% / 58% HSL. */
function hueFill(hue: number): Rgb {
  const h = ((hue % 360) + 360) % 360;
  const s = 0.85;
  const l = 0.58;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}

export type AvatarMirror = "auto" | "horizontal" | "vertical";

type AvatarModel = {
  /** GRID×GRID, row-major. */
  on: boolean[];
  /** Per-cell dither density, for the cells that are on. */
  density: number[];
  fill: Rgb;
};

/**
 * Derive the whole 8×8 grid from the name: 32 pattern bits, the mirror axis,
 * the hue and the per-cell densities, all off one PRNG stream.
 *
 * Every value is drawn unconditionally even when it is then overridden, so
 * passing `hue` or `mirror` re-colours or re-folds the glyph without shifting
 * the pattern out from under it.
 */
function avatarModel(
  name: string,
  hueProp: number | undefined,
  mirrorProp: AvatarMirror,
): AvatarModel {
  const rand = xorshift32(fnv1a(name));
  const bits = Array.from({ length: 32 }, () => rand() < 0.5);
  const drawnVertical = rand() < 0.5;
  const drawnHue = Math.floor(rand() * 180) * 2;
  const halfDensity = Array.from({ length: 32 }, () => 0.55 + rand() * 0.45);

  const vertical = mirrorProp === "auto" ? drawnVertical : mirrorProp === "vertical";
  const hue = hueProp ?? drawnHue;

  const on = new Array<boolean>(GRID * GRID);
  const density = new Array<number>(GRID * GRID);
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      // Fold across the chosen axis: left/right symmetric, or top/bottom.
      const i = vertical
        ? Math.min(r, GRID - 1 - r) * GRID + c
        : r * (GRID / 2) + Math.min(c, GRID - 1 - c);
      on[r * GRID + c] = bits[i]!;
      density[r * GRID + c] = halfDensity[i]!;
    }
  }
  return { on, density, fill: hueFill(hue) };
}

/**
 * Alpha of a lit sub-pixel in a cell of this density. On and off sub-pixels are
 * alpha tiers of the *one* fill colour rather than two colours, which is what
 * lets the glyph hold up on paper and on ink without a second palette.
 */
const litAlpha = (density: number) => 0.35 + 0.65 * density;
/** An off sub-pixel is a tint of the same ink, never a hole. */
const OFF_TIER = 0.35;

/**
 * The alpha a whole cell averages to, for the vector renderer.
 *
 * The Bayer thresholds are the 16 values (v + 0.5) / 16, spread evenly across
 * 0–1, so the share of sub-pixels a density lights is that density itself. The
 * cell's mean ink is therefore the lit tier weighted by `d` plus the off tier
 * weighted by the rest — which is what a 4×4 dither looks like once it is
 * smaller than a pixel.
 */
const cellAlpha = (density: number) =>
  litAlpha(density) * (density + OFF_TIER * (1 - density));

/**
 * Paint the avatar, optionally sweeping the cells in. Lives outside the
 * component; returns a cleanup that cancels the entrance loop.
 */
function paintAvatar(
  canvas: HTMLCanvasElement,
  model: AvatarModel,
  animate: boolean,
  duration: number,
): (() => void) | undefined {
  const ctx = canvas.getContext("2d");
  if (!ctx) return undefined;
  const px = GRID * CELL_PX;
  canvas.width = px;
  canvas.height = px;

  const draw = (progress: number) => {
    ctx.clearRect(0, 0, px, px);
    for (let r = 0; r < GRID; r++) {
      for (let c = 0; c < GRID; c++) {
        if (!model.on[r * GRID + c]) continue;
        // Cells materialise in Bayer order — the entrance is made of the same
        // matrix as the texture.
        const start = BAYER4[r % 4]![c % 4]! * 0.7;
        const entrance = clamp01((progress - start) / 0.3);
        if (entrance <= 0) continue;
        const density = model.density[r * GRID + c]!;
        const base = litAlpha(density);
        for (let py = 0; py < CELL_PX; py++) {
          for (let pxi = 0; pxi < CELL_PX; pxi++) {
            const gx = c * CELL_PX + pxi;
            const gy = r * CELL_PX + py;
            const lit = density > BAYER4[gy & 3]![gx & 3]!;
            ctx.fillStyle = rgba(model.fill, (lit ? base : base * OFF_TIER) * entrance);
            ctx.fillRect(gx, gy, 1, 1);
          }
        }
      }
    }
  };

  if (!animate || reduceMotion()) {
    draw(1);
    return undefined;
  }

  let raf = 0;
  const started = performance.now();
  const tick = (now: number) => {
    const t = clamp01((now - started) / duration);
    draw(1 - (1 - t) ** 3);
    if (t < 1) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

export type DitherAvatarProps = {
  /** The seed — same name, same avatar, every time. */
  name: string;
  /** Hue override (0–360). Derived from the name when omitted. */
  hue?: number;
  /** Mirror axis. "auto" takes it from the name, so half of them fold each way. */
  mirror?: AvatarMirror;
  /** Square size in px. */
  size?: number;
  /** Play the Bayer-ordered materialize entrance. */
  animate?: boolean;
  animationDuration?: number;
  /** Bump to replay the entrance. */
  replayToken?: number;
  /** Empty for a decorative avatar sitting next to the name in text. */
  label?: string;
  className?: string;
};

/**
 * A name-seeded avatar, painted on canvas at full dither fidelity.
 *
 * `label` is the accessible name. It defaults to the seed, which is right when
 * the avatar stands alone; pass `""` when the name is already next to it in
 * text, so a screen reader does not read it twice.
 */
export function DitherAvatar({
  name,
  hue,
  mirror = "auto",
  size = 40,
  animate = true,
  animationDuration = 600,
  replayToken = 0,
  label,
  className,
}: DitherAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    return paintAvatar(canvas, avatarModel(name, hue, mirror), animate, animationDuration);
  }, [name, hue, mirror, animate, animationDuration, replayToken]);

  const described = label ?? name;

  return (
    <span
      className={className ? `dither-avatar ${className}` : "dither-avatar"}
      style={{ width: size, height: size }}
      role={described ? "img" : undefined}
      aria-label={described || undefined}
      aria-hidden={described ? undefined : true}
    >
      <canvas className="dither-avatar__canvas" ref={canvasRef} />
    </span>
  );
}

export type DitherMarkProps = {
  name: string;
  hue?: number;
  mirror?: AvatarMirror;
  size?: number;
  /** Accessible name. Omitted marks are decorative and hidden. */
  label?: string;
};

/**
 * The same glyph as vector, one rect per cell — for the brand mark, which is
 * drawn small enough that the sub-cell texture is below a pixel and a scaled
 * canvas would only be blurry. See {@link cellAlpha}.
 */
export function DitherMark({
  name,
  hue,
  mirror = "auto",
  size = 18,
  label,
}: DitherMarkProps) {
  const model = avatarModel(name, hue, mirror);
  const cells: React.ReactElement[] = [];
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const i = r * GRID + c;
      if (!model.on[i]) continue;
      cells.push(
        <rect
          key={i}
          x={c * CELL_PX}
          y={r * CELL_PX}
          width={CELL_PX}
          height={CELL_PX}
          fillOpacity={cellAlpha(model.density[i]!).toFixed(3)}
        />,
      );
    }
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${GRID * CELL_PX} ${GRID * CELL_PX}`}
      fill={rgba(model.fill, 1)}
      shapeRendering="crispEdges"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label ? <title>{label}</title> : null}
      {cells}
    </svg>
  );
}
